import type { GridPos } from "../protocol/snapshot";
import { assertNever } from "../util/assertNever";
import type { CardDef } from "./definitions";
import type { EngineState } from "./state";

/**
 * The single reader of held hazard cards (spec 0007 Mechanism table). Every
 * caller that needs to know what a hand's hazard cards are currently doing —
 * `advancePhase`'s Play entry, `playCard`, `deployPipe`/`applyHardware`, and
 * `projectSnapshot` — goes through this rather than re-scanning `state.hand`
 * itself, so the HUD and the engine's own gates can never disagree.
 *
 * Pure, no RNG, O(hand) — a hand is at most 5-6 cards.
 */
export interface HeldPenalties {
  readonly powerPenaltyKw: number;
  readonly powerDrain: boolean;
  readonly directiveSurchargeKw: number;
  /** One entry per held Clogged Line, its tile from `state.cloggedLineLockedTiles` (spec 0016, G-F7). */
  readonly lockedTiles: readonly GridPos[];
  /** One entry per held Chemical Hazard Spill — duplicates if more than one copy is held. */
  readonly lockedHandInstanceIds: readonly string[];
}

export function heldPenalties(state: EngineState, cards: Record<string, CardDef>): HeldPenalties {
  let powerPenaltyKw = 0;
  let powerDrain = false;
  let directiveSurchargeKw = 0;
  const lockedTiles: GridPos[] = [];
  const lockedHandInstanceIds: string[] = [];

  for (const instance of state.hand) {
    const card = cards[instance.cardId];
    if (!card || card.category !== "hazard" || !card.hazard) continue;

    switch (card.hazard.kind) {
      case "powerPenalty":
        powerPenaltyKw += card.hazard.kw;
        break;
      case "powerDrain":
        powerDrain = true;
        break;
      case "directiveSurcharge":
        directiveSurchargeKw += card.hazard.kw;
        break;
      case "tileLock": {
        // The tile is fixed on the instance at the moment `pollute()` injected
        // it (spec 0016, G-F7) — no longer derived by matching hand position
        // against a shared list of surviving sludge sources.
        const tile = state.cloggedLineLockedTiles[instance.instanceId];
        if (tile) lockedTiles.push(tile);
        break;
      }
      case "handSlotLock":
        lockedHandInstanceIds.push(instance.instanceId);
        break;
      // Read elsewhere while held, or only at the moment they're left unplayed
      // (see the Mechanism table): drawPenalty and pipeStress are charged by
      // applyUnplayedHazards, maintenanceFee is read by @reagent/meta at stage
      // end, and infestation is a plain dead draw (spec 0033 owner decision 8).
      case "drawPenalty":
      case "maintenanceFee":
      case "infestation":
      case "pipeStress":
        break;
      default:
        assertNever(card.hazard, "heldPenalties");
    }
  }

  return { powerPenaltyKw, powerDrain, directiveSurchargeKw, lockedTiles, lockedHandInstanceIds };
}

/**
 * `card.costKw` plus any directiveSurcharge that applies to it this turn.
 * Keyed off `card.directive` being present rather than `card.category ===
 * "directive"` (spec 0020 OQ2): `metaUtility` cards carry the same
 * `directive` payload and must keep surcharging exactly as before their
 * recategorisation, without hardcoding which cards moved.
 */
export function effectiveCostKw(state: EngineState, cards: Record<string, CardDef>, card: CardDef): number {
  if (!card.directive) return card.costKw;
  return card.costKw + heldPenalties(state, cards).directiveSurchargeKw;
}
